import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2, H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { ROUTE } from "../../../routes";
import { Image } from "../../../components/Image";
import editIcon from "../../../assets/settings/edit.svg";
import { useForms } from "../../../data/useForms";
import { Loader } from "../../../components/Loader";
import React, { useEffect, useMemo, useState } from "react";
import CustomTabs from "../../../components/CustomTabs";
import { HiOutlineUser, HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useBillingInfo } from "../../../data/useBillingInfo";

type TypeFilter = "individual" | "entity";

type FormItem = {
  id: number;
  name: string;
  description?: string;
  _type: TypeFilter;
};

const SettingsForms = () => {
  const navigate = useNavigate();
  const { data, isLoading: formsLoading } = useForms();

  const { data: billingData, isLoading: billingLoading } = useBillingInfo();
  const billingInfo =
    billingData?.data?.results?.length > 0 ? billingData.data.results[0] : null;

  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!profilesEnabled && activeTab !== 0) setActiveTab(0);
  }, [profilesEnabled, activeTab]);

  const viewType: TypeFilter =
    profilesEnabled && activeTab === 1 ? "entity" : "individual";

  const handleEdit = (id: number) => {
    navigate(`/settings/forms/${id}`);
  };

  const allForms = useMemo<FormItem[]>(() => (data ?? []) as FormItem[], [data]);

  const filteredForms = useMemo(
    () => allForms.filter((item) => item._type === viewType),
    [allForms, viewType],
  );

  const tabs = useMemo(() => {
    const base = [
      {
        label: (
          <>
            <HiOutlineUser size={16} />
            <span>Individual</span>
          </>
        ),
        content: null,
      },
    ];

    if (!profilesEnabled) return base;

    return [
      ...base,
      {
        label: (
          <>
            <HiOutlineBuildingOffice2 size={16} />
            <span>Entity</span>
          </>
        ),
        content: null,
      },
    ];
  }, [profilesEnabled]);

  const pageLoading = formsLoading || billingLoading;

  return (
    <>
      {pageLoading && (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      )}

      {!pageLoading && (
        <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
          <Flex justifyContent="space-between" alignItems="center">
            <H2 fontSize="3">Forms</H2>

            <Button
              Icon={FaPlus}
              iconSize="16px"
              onClick={() => navigate(ROUTE.SETTINGS_FORM_NEW)}
            >
              New Form
            </Button>
          </Flex>

          <CustomTabs
            activeTab={activeTab}
            onTabChange={(i: number) => setActiveTab(i)}
            customMarginLeft="0"
            variant="segmented"
            tabs={tabs}
          />

          {/* List */}
          {filteredForms.length === 0 ? (
            <Paragraph color="gray.60" mt={3}>
              No {viewType} forms found.
            </Paragraph>
          ) : (
            filteredForms.map((item) => (
              <Flex
                key={item.id}
                py={2}
                borderBottom="1px solid #e0e0e0"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <H3 fontSize="16px">{item.name}</H3>
                  <Paragraph fontSize={1} my={0} color="gray.60">
                    {item.description}
                  </Paragraph>
                </Box>
                <Box>
                  <Image
                    src={editIcon}
                    alt="edit icon"
                    height="22px"
                    width="22px"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEdit(item.id)}
                  />
                </Box>
              </Flex>
            ))
          )}
        </Box>
      )}
    </>
  );
};

export default SettingsForms;
